import { ChangeEvent, FC, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { useStyles } from "./style";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import {
  ActiveDATimeRangeGraph,
  ActiveFleetsTimeRangeGraph,
  DeliveryTimeRangeGraph,
  RejectedGraph,
  ReturnsTotalsGraph,
  ReturnsTrendsGraph,
  RolesTimeRangeGraph,
  RoleTotalsGraph,
} from "./dashboardContent/DashboardStatsGraphs";
import { DashboardModalWindow } from "componentsNew/modals/DashboardModalWindow";
import { useDisplayDashboardFilters } from "hooks/useDisplayDashboardFilters";
import Fullscreen from "@material-ui/icons/Fullscreen";
import Error from "@material-ui/icons/ErrorOutline";
import { AlertBlock } from "componentsNew/elements/AlertBlock";
import { Color } from "@material-ui/lab";
import { DashboardTableList } from "./dashboardContent/DashboardTableList";
import {
  daTableColumns,
  dollyTableColumns,
  fleetTableColumns,
  rabbitTableColumns,
} from "./tableData";
import { formatNumber } from "utils/formatNumbers";
import Loader from "router/components/Loader";
import { handleGetRejectedShiftsStats } from "./requests";
import { updateRejectedDAStatusChartFilters } from "actions/dashboardActions";
import { DriverData } from "types/ShiftTracker";
import { useDispatch } from "react-redux";
import api from "api";

interface StatsBlockOneProps {
  data: any;
  hasBottomBorder?: boolean;
  hasSecondTitle?: boolean;
  hasModalOne?: boolean;
  hasModalTwo?: boolean;
}

interface RejectedGraphBlockProps {
  title: string;
  hasDravierFilter?: boolean;
}

interface MainBlockProps {
  data: any;
}

export const MainBlock: FC<MainBlockProps> = ({ data }) => {
  const classes = useStyles();
  const combinedFilterString = useDisplayDashboardFilters();

  return (
    <Box className={`${classes.mainBlock}`}>
      <Box className={classes.mainLeftSection}>
        <Typography style={{ fontSize: 18, fontWeight: 500 }}>Operational Routes</Typography>
        <Box className={classes.totalsList}>
          {data?.previewLabels?.map(
            (item: { name: string; color: string; count: number }, index: number) => (
              <Box
                className={`${classes.totalsRow} ${data?.previewLabels?.length - 1 !== index && classes.bottomBorder}`}
                key={item.name}
              >
                <Box className={classes.totalsRowLeft}>
                  <Box className={classes.totalsRowVisual} style={{ background: item.color }}></Box>
                  <Typography className={classes.totalsRowName}>{item.name}</Typography>
                </Box>
                <Typography className={classes.totalsRowCount}>
                  {formatNumber(item.count)}
                </Typography>
              </Box>
            ),
          )}
        </Box>
        <Box className={classes.bottomModal}>
          <DashboardModalWindow
            modal_name={"main_dashboard_role_totals_stats"}
            buttonType={"text"}
            buttonText={"View All Routes"}
            modalTitle={"All Routes"}
            modalDescription={combinedFilterString}
            hasGraph
          >
            <RoleTotalsGraph data={data.current} margin={{ right: 32, left: 170 }} height={440} />
          </DashboardModalWindow>
        </Box>
      </Box>
      <Box className={classes.mainRightSection}>
        <Box className={classes.mainRightSectionHead}>
          <Typography style={{ fontSize: 18, fontWeight: 500 }}>
            Operational Routes Trends
          </Typography>
          <DashboardModalWindow
            modal_name={"main_dashboard_role_stats"}
            buttonType={"custom"}
            buttonText={"View All Routes"}
            modalTitle={"All Routes"}
            modalDescription={combinedFilterString}
            customButtonElement={<Fullscreen style={{ color: "#5F6164" }} />}
            hasGraph
          >
            <RolesTimeRangeGraph
              data={data.prev_list}
              margin={{ left: 80, right: 80 }}
              height={440}
            />
          </DashboardModalWindow>
        </Box>
        <RolesTimeRangeGraph data={data.prev_list} margin={{ right: 32 }} height={440} hideLegend />
      </Box>
    </Box>
  );
};

export const StatsBlock: FC<StatsBlockOneProps> = ({
  data,
  hasBottomBorder,
  hasSecondTitle,
  hasModalOne,
  hasModalTwo,
}) => {
  const classes = useStyles();
  const { dashboardSettings } = useTypeSelectors((state) => state.dashboard);
  const combinedFilterString = useDisplayDashboardFilters();

  return (
    <Box className={classes.blockRoot}>
      <Box
        className={`${classes.totalsSection} ${hasBottomBorder && !hasModalOne ? classes.bottomBorder : ""}`}
      >
        <Box className={classes.statsBox}>
          <Typography className={classes.statsTitle}>{data.title}</Typography>
          <Typography className={classes.statsCount}>{data.total}</Typography>
        </Box>
        <Box className={classes.statsBox}>
          <Typography className={classes.smallName}>{data.smallText}</Typography>
          <Typography>
            <AlertBlock text={data.smallValue} theme={data.alertTheme as Color} />
          </Typography>
        </Box>
      </Box>
      {hasModalOne && (
        <Box className={`${classes.bottomModal} ${hasBottomBorder && classes.bottomBorder}`}>
          <DashboardModalWindow
            modal_name={`${data.modalOneName}-stats`}
            buttonType={"text"}
            buttonText={data.modalOneBtn}
            modalTitle={`${data?.modalOneName}`}
            modalDescription={combinedFilterString}
            hasGraph
          >
            {returnStatsGraphsOne(data?.tag, data)}
          </DashboardModalWindow>
        </Box>
      )}
      <Box className={classes.statsSection}>
        {hasSecondTitle && (
          <Typography className={classes.secondTitle}>{data.secondTitle}</Typography>
        )}
        <Box className={classes.statsList}>
          {data?.threeStats?.map((item: any, index: number) => (
            <Box
              key={item.name}
              className={`${classes.statsRow} ${index !== data?.threeStats.length - 1 && classes.bottomBorder}`}
            >
              <Box className={classes.statsRowLeft}>
                <Typography>{item.name}</Typography>
                {item.hasList && dashboardSettings.filterType !== "year" ? (
                  <DashboardModalWindow
                    modal_name={`${item.modalName}-stats`}
                    buttonType={"custom"}
                    modalTitle={item.modalName}
                    modalDescription={combinedFilterString}
                    customButtonElement={<Error style={{ color: "#0129FA" }} />}
                    isDisabled={!item.list.length}
                  >
                    {returnRowGraphs(data.tag, item.list)}
                  </DashboardModalWindow>
                ) : (
                  ""
                )}
              </Box>
              <Typography className={classes.rowCount}>{item.count}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {hasModalTwo && (
        <Box className={classes.bottomModal}>
          <DashboardModalWindow
            modal_name={`${data.modalTwoName}-stats`}
            buttonType={"text"}
            buttonText={data.modalTwoBtn}
            modalTitle={`${data?.modalTwoName}`}
            modalDescription={combinedFilterString}
            hasGraph
          >
            {returnStatsGraphsTwo(data?.tag, data)}
          </DashboardModalWindow>
        </Box>
      )}
    </Box>
  );
};

export const CardBlock: FC<StatsBlockOneProps> = ({ data, hasModalOne, hasModalTwo }) => {
  const classes = useStyles();
  const combinedFilterString = useDisplayDashboardFilters();

  return (
    <Box className={classes.blockRoot}>
      <Box className={`${classes.totalsSection}`}>
        <Box className={classes.statsBox}>
          <Typography className={classes.statsTitle}>{data.title}</Typography>
          <Typography className={classes.statsCount}>{data.total}</Typography>
        </Box>
        <Box className={classes.statsBox}>
          <Typography className={classes.smallName}>{data.smallText}</Typography>
          <Typography>
            <AlertBlock text={data.smallValue} theme={data.alertTheme as Color} />
          </Typography>
        </Box>
      </Box>
      <Box className={classes.modalSection}>
        {hasModalOne && (
          <Box className={`${classes.bottomModal}`}>
            <DashboardModalWindow
              modal_name={`${data.modalOneName}-stats`}
              buttonType={"text"}
              buttonText={data.modalOneBtn}
              modalTitle={`${data?.modalOneName}`}
              modalDescription={combinedFilterString}
              hasGraph
            >
              {returnStatsGraphsOne(data?.tag, data)}
            </DashboardModalWindow>
          </Box>
        )}
        {hasModalTwo && (
          <Box className={classes.bottomModal}>
            <DashboardModalWindow
              modal_name={`${data.modalTwoName}-stats`}
              buttonType={"text"}
              buttonText={data.modalTwoBtn}
              modalTitle={`${data?.modalTwoName}`}
              modalDescription={combinedFilterString}
              hasGraph
            >
              {returnStatsGraphsTwo(data?.tag, data)}
            </DashboardModalWindow>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const RejectedGraphBlock: FC<RejectedGraphBlockProps> = ({ title, hasDravierFilter }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState<DriverData[]>([]);
  const [requestData, setRequestData] = useState<any | null>(null);
  const {
    dashboardSettings,
    dashboardSettings: { rejectedDaFilters },
  } = useTypeSelectors((state) => state.dashboard);

  useEffect(() => {
    const params = {
      ...(dashboardSettings?.filterType === "month" && {
        month: +Number(dashboardSettings.dateFilters?.month.split("-")[1]),
        year: +Number(dashboardSettings.dateFilters?.month.split("-")[0]),
      }),
      ...(dashboardSettings?.filterType === "week" && {
        week: +Number(dashboardSettings.dateFilters?.week.split("-W")[1]) - 1,
        year: +Number(dashboardSettings.dateFilters?.week.split("-W")[0]),
      }),
      ...(dashboardSettings?.filterType === "year" && {
        year: +Number(dashboardSettings.dateFilters?.year),
      }),
      ...(hasDravierFilter && {
        driver: rejectedDaFilters?.driver?.id,
      }),
    };
    handleGetRejectedShiftsStats(params, setLoading, setRequestData);
  }, [
    hasDravierFilter,
    dashboardSettings.dateFilters?.month,
    dashboardSettings.dateFilters?.week,
    dashboardSettings.dateFilters?.year,
    dashboardSettings?.filterType,
    rejectedDaFilters?.driver?.id,
  ]);

  const handleGetDrivers = async () => {
    setLoading(true);
    api
      .getAvailableDrivers({
        available: true,
      })
      .then((res) => {
        if (res?.data?.results) {
          setDrivers(res.data.results);
          setLoading(false);
        }
      })
      .catch(() => {
        handleClearDrivers();
      });
  };

  const handleClearDrivers = () => {
    setDrivers([]);
    setLoading(false);
  };

  const handleChangeDriver = (event: ChangeEvent<{ value: any }>) => {
    const driverId = event.target.value;
    if (driverId) {
      const currentDriver = drivers.find((el: DriverData) => el.id === +driverId);
      handleClearDrivers();
      dispatch(updateRejectedDAStatusChartFilters({ driver: currentDriver }));
    } else {
      dispatch(updateRejectedDAStatusChartFilters({ driver: null }));
    }
  };

  const driverLabel = rejectedDaFilters?.driver?.id ? (
    <MenuItem key={rejectedDaFilters?.driver?.id} value={rejectedDaFilters?.driver?.id}>
      <ListItemText
        primary={`${rejectedDaFilters?.driver?.first_name} ${rejectedDaFilters?.driver?.last_name}`}
      />
    </MenuItem>
  ) : (
    <MenuItem key={0} value={0}>
      <ListItemText primary={"Select Driver"} />
    </MenuItem>
  );

  return (
    <Box className={classes.blockRoot}>
      <Loader isLoading={loading} />
      <Box className={`${classes.totalsSection}`}>
        <Box className={classes.statsBox}>
          <Typography className={classes.statsTitle}>{title}</Typography>
        </Box>
      </Box>
      {hasDravierFilter && (
        <Box className={classes.driverFilterBox}>
          <Box>
            <Typography className={classes.driverLavel}>Select DA</Typography>
            <Select
              className={classes.filterSelect}
              variant="outlined"
              value={rejectedDaFilters?.driver?.id || rejectedDaFilters?.driver || 0}
              onOpen={handleGetDrivers}
              onChange={handleChangeDriver}
              onClose={handleClearDrivers}
            >
              <MenuItem className={classes.filterOption} key={0} value={0}>
                <ListItemText primary={"Unassigned driver"} />
              </MenuItem>
              {drivers.length === 0 ? driverLabel : null}
              {loading ? (
                <MenuItem className={classes.filterOption} key="loading" disabled>
                  <CircularProgress />
                </MenuItem>
              ) : (
                drivers.map((el: DriverData) => (
                  <MenuItem className={classes.filterOption} key={el.id} value={el.id}>
                    <ListItemText primary={`${el.first_name} ${el.last_name}`} />
                  </MenuItem>
                ))
              )}
            </Select>
          </Box>
          <Box>
            <Typography className={classes.driverLavel}>Badge</Typography>
            <Typography>{rejectedDaFilters?.driver?.badge}</Typography>
          </Box>
        </Box>
      )}
      <Box>
        <RejectedGraph
          data={dashboardSettings.filterType !== "date" ? requestData || [] : []}
          margin={{ left: 20, right: 20 }}
          height={hasDravierFilter ? 340 : 400}
        />
      </Box>
    </Box>
  );
};

const returnStatsGraphsOne = (tag: string, data: any) => {
  switch (tag) {
    case "deliveries":
      return (
        <DeliveryTimeRangeGraph
          data={data.prev_list}
          margin={{ left: 80, right: 50 }}
          height={440}
          hideLegend
        />
      );
    case "returns":
      return (
        <ReturnsTrendsGraph
          data={data.period_chart_data}
          margin={{ left: 80, right: 180 }}
          height={500}
        />
      );
    default:
      break;
  }
};

const returnRowGraphs = (tag: string, dataList: any) => {
  switch (tag) {
    case "fleet":
      return <DashboardTableList tableData={fleetTableColumns} data={dataList} />;
    case "da":
      return <DashboardTableList tableData={daTableColumns} data={dataList} />;
    case "rabbit":
      return <DashboardTableList tableData={rabbitTableColumns} data={dataList} />;
    case "dolly":
      return <DashboardTableList tableData={dollyTableColumns} data={dataList} />;
    default:
      break;
  }
};

const returnStatsGraphsTwo = (tag: string, data: any) => {
  switch (tag) {
    case "returns":
      return (
        <ReturnsTotalsGraph
          data={data.bar_chart_data}
          margin={{ left: 90, right: 32 }}
          height={440}
        />
      );
    case "da":
      return (
        <ActiveDATimeRangeGraph
          data={data.period_chart_data}
          margin={{ left: 90, right: 32 }}
          height={440}
          hideLegend
        />
      );
    case "fleet":
      return (
        <ActiveFleetsTimeRangeGraph
          data={data.period_chart_data}
          margin={{ left: 90, right: 32 }}
          height={440}
          hideLegend
        />
      );
    default:
      break;
  }
};
